/**
 * Memo templates and the code that works on them, kept apart: `templates/` is
 * the shipped memos as data, `lib/` is what validates, resolves and renders
 * them, and this file is where the two meet.
 */
import { memoTemplateSchema, type MemoTemplate } from './lib/schema.js'

import nightFive from './templates/night-five.json' with { type: 'json' }
import toEditors from './templates/to-editors.json' with { type: 'json' }
import morningPm from './templates/morning-pm.json' with { type: 'json' }
import updatedPm from './templates/updated-pm.json' with { type: 'json' }
import printPm from './templates/print-pm.json' with { type: 'json' }
import weeklyPm from './templates/weekly-pm.json' with { type: 'json' }
import futurePm from './templates/future-pm.json' with { type: 'json' }
import weekendPm from './templates/weekend-pm.json' with { type: 'json' }
import tomorrowPm from './templates/tomorrow-pm.json' with { type: 'json' }
import empty from './templates/empty.json' with { type: 'json' }

// The creation dialog's picker lists these by label, so the order here is
// only the one they are validated in.
const raw = [
  empty, toEditors, nightFive, morningPm, updatedPm, tomorrowPm, printPm, weekendPm, weeklyPm,
  futurePm
]

/**
 * All shipped memo templates, validated at module load so a malformed template
 * fails loudly during development rather than silently in the editor.
 */
export const memoTemplates: MemoTemplate[] = raw.map((template) => {
  const parsed = memoTemplateSchema.safeParse(template)

  if (!parsed.success) {
    throw new Error(
      `Invalid memo template ${JSON.stringify((template as { id?: string }).id)}: ${parsed.error.message}`
    )
  }

  return parsed.data
})

export function getMemoTemplate(id: string): MemoTemplate | undefined {
  return memoTemplates.find((template) => template.id === id)
}

/**
 * The block a template's candidates are mapped into as they are fetched, which
 * is where they wait rather than where they end up: the editor answers for a
 * pick on the batch bar while more than one block is active, and a pick made
 * with one block left resolves into that one. Usually the first block, but a
 * template whose first block is one an editor opts an item *into* - the weekend
 * PM's "Toppnyheter", which renders first within each day - names another.
 */
export function defaultBlockId(template: MemoTemplate): string {
  return template.defaultBlockId ?? template.blocks[0].id
}

export * from './lib/columns.js'
export * from './lib/planning.js'
export * from './lib/schema.js'
